import React from "react";

import { Tooltip } from "src/design-system/Tooltip";

/**
 * Returns true if the routing target region is "Global".
 */
export const isGlobalRouting = (
  routingTargetRegion: string | null | undefined,
): boolean => routingTargetRegion === "Global";

type RegionRoutingBadgeProps = {
  targetRegion: string;
};

/**
 * Badge indicating that a model is processed in a different region.
 */
export const RegionRoutingBadge: React.FC<RegionRoutingBadgeProps> = ({
  targetRegion,
}) => {
  let description = "This model is not available in your region. ";
  if (targetRegion === "Global") {
    description +=
      "Requests will be processed globally to ensure availability.";
  } else {
    description += `Requests will be processed in the ${targetRegion} to ensure availability.`;
  }

  return (
    <Tooltip
      body={
        <div>
          <div className="mb-1">{description}</div>
          <a
            className="text-indigo-400 underline hover:text-indigo-300"
            href="https://docs.taktile.com/decision-flow-fundamentals/node-types/ai-node#model-selection"
            rel="noopener noreferrer"
            target="_blank"
          >
            Learn more
          </a>
        </div>
      }
      placement="right"
      asChild
    >
      <div className="flex h-5 shrink-0 items-center rounded bg-yellow-100 px-2 py-1">
        <span className="font-inter-normal-12px text-yellow-700">
          Processed{" "}
          {targetRegion === "Global" ? "globally" : `in ${targetRegion}`}
        </span>
      </div>
    </Tooltip>
  );
};

/**
 * Badge indicating that a model is only available via a global endpoint.
 * regardless of the workspace region.
 * Uses the same visual design as RegionRoutingBadge with "Global" as the region.
 */
export const GlobalBadge: React.FC = () => {
  return (
    <Tooltip
      body={
        <div>
          <div className="mb-1">
            This model is only available via a global endpoint
          </div>
          <a
            className="text-indigo-400 underline hover:text-indigo-300"
            href="https://docs.taktile.com/decision-flow-fundamentals/node-types/ai-node#model-selection"
            rel="noopener noreferrer"
            target="_blank"
          >
            Learn more
          </a>
        </div>
      }
      placement="right"
      asChild
    >
      <div className="flex h-5 shrink-0 items-center rounded bg-yellow-100 px-2 py-1">
        <span className="font-inter-normal-12px text-yellow-700">
          Processed globally
        </span>
      </div>
    </Tooltip>
  );
};
